import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { FileManagerService } from './file-manager.service';
import type { FileWithPath, CollectResult, UploadEvent, UploadFileStatus } from '../models/upload.model';

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?: (resolve: (file: File) => void, reject: (err: unknown) => void) => void;
  createReader?: () => { readEntries: (resolve: (entries: FileSystemEntryLike[]) => void, reject: (err: unknown) => void) => void };
}

@Injectable({ providedIn: 'root' })
export class UploadService {
  private readonly api = inject(FileManagerService);

  async collectFromDataTransfer(items: DataTransferItemList): Promise<CollectResult> {
    const collected: FileWithPath[] = [];
    const failed: string[] = [];
    const entries: FileSystemEntryLike[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = (item as unknown as { webkitGetAsEntry: () => FileSystemEntryLike | null }).webkitGetAsEntry();
        if (entry) entries.push(entry);
      }
    }

    for (const entry of entries) {
      await this.traverseEntry(entry, collected, '', failed);
    }
    return { files: collected, failed };
  }

  uploadFilesSequentially(targetPath: string, files: FileWithPath[]): Observable<UploadEvent> {
    return new Observable<UploadEvent>((observer) => {
      let cancelled = false;

      (async () => {
        if (files.length === 0) {
          observer.next({ kind: 'all-done' });
          observer.complete();
          return;
        }

        const { groups, fileToGroup, fileToChild } = this.buildGroups(files, targetPath);
        observer.next({ kind: 'init', groups });

        const createdFolders = new Set<string>();
        const groupErrors = new Array(groups.length).fill(0);
        const groupCompleted = new Array(groups.length).fill(0);
        let completedFiles = 0;

        for (let i = 0; i < files.length; i++) {
          if (cancelled) return;
          const item = files[i];
          const groupIdx = fileToGroup[i];
          const childIdx = fileToChild[i];
          let fileTargetPath = targetPath;

          if (item.relativePath) {
            let acc = targetPath;
            for (const folder of item.relativePath.split('/')) {
              if (!folder) continue;
              const folderPath = `${acc}/${folder}`;
              if (!createdFolders.has(folderPath)) {
                try { await firstValueFrom(this.api.createFolder(acc, folder)); } catch {}
                createdFolders.add(folderPath);
              }
              acc = folderPath;
            }
            fileTargetPath = acc;
          }

          observer.next({ kind: 'group', index: groupIdx, patch: { status: 'uploading' } });
          if (childIdx >= 0) {
            observer.next({ kind: 'child', groupIndex: groupIdx, childIndex: childIdx, patch: { status: 'uploading' } });
          }

          let fileSucceeded = false;
          try {
            await new Promise<void>((resolve, reject) => {
              this.api.uploadFiles(fileTargetPath, [item.file]).subscribe({
                next: (event) => {
                  const overall = Math.round(
                    (completedFiles / files.length) * 100 + event.progress / files.length,
                  );
                  observer.next({ kind: 'progress', overall });
                  if (event.data) resolve();
                },
                error: reject,
              });
            });
            fileSucceeded = true;
          } catch (err) {
            console.error('Failed to upload file', item.file.name, err);
          }

          completedFiles++;
          if (!fileSucceeded) groupErrors[groupIdx]++;
          groupCompleted[groupIdx]++;
          if (childIdx >= 0) {
            observer.next({ kind: 'child', groupIndex: groupIdx, childIndex: childIdx, patch: { status: fileSucceeded ? 'success' : 'error' } });
          }
          const groupTotal = groups[groupIdx].total;
          const allDone = groupCompleted[groupIdx] >= groupTotal;
          observer.next({
            kind: 'group',
            index: groupIdx,
            patch: {
              completed: groupCompleted[groupIdx],
              status: allDone ? (groupErrors[groupIdx] > 0 ? 'error' : 'success') : 'uploading',
            },
          });
        }

        observer.next({ kind: 'all-done' });
        observer.complete();
      })().catch((err) => observer.error(err));

      return () => { cancelled = true; };
    });
  }

  private buildGroups(
    files: FileWithPath[],
    targetPath: string,
  ): { groups: UploadFileStatus[]; fileToGroup: number[]; fileToChild: number[] } {
    const groups: UploadFileStatus[] = [];
    const groupKeyToIndex = new Map<string, number>();
    const fileToChild: number[] = [];
    const fileToGroup = files.map((f) => {
      const topFolder = f.relativePath ? f.relativePath.split('/')[0] : '';
      const isFolder = !!topFolder;

      const key = isFolder
        ? `folder:${targetPath}:${topFolder}`
        : `file:${targetPath}:${f.file.name}`;
      let idx = groupKeyToIndex.get(key);
      if (idx === undefined) {
        idx = groups.length;
        groupKeyToIndex.set(key, idx);
        groups.push({
          id: key,
          name: isFolder ? topFolder : f.file.name,
          isFolder,
          completed: 0,
          total: 0,
          status: 'pending',
          children: isFolder ? [] : undefined,
        });
      }
      if (isFolder) {
        const childIndex = groups[idx].children!.length;
        groups[idx].children!.push({
          id: `${key}:${f.relativePath}/${f.file.name}`,
          name: f.file.name,
          isFolder: false,
          completed: 0,
          total: 1,
          status: 'pending',
        });
        fileToChild.push(childIndex);
      } else {
        fileToChild.push(-1);
      }
      groups[idx].total++;
      return idx;
    });
    return { groups, fileToGroup, fileToChild };
  }

  private async traverseEntry(
    entry: FileSystemEntryLike,
    files: FileWithPath[],
    currentPath: string,
    failed: string[],
  ): Promise<void> {
    if (entry.isFile && entry.file) {
      try {
        const file = await new Promise<File>((resolve, reject) => entry.file!(resolve, reject));
        files.push({ file, relativePath: currentPath });
      } catch (err) {
        const fullName = currentPath ? `${currentPath}/${entry.name}` : entry.name;
        console.warn('Skipping unreadable dropped file', fullName, err);
        failed.push(fullName);
      }
    } else if (entry.isDirectory && entry.createReader) {
      const reader = entry.createReader();
      let allEntries: FileSystemEntryLike[] = [];
      const readBatch = (): Promise<FileSystemEntryLike[]> =>
        new Promise((resolve, reject) => reader.readEntries(resolve, reject));

      try {
        let batch = await readBatch();
        while (batch.length > 0) {
          allEntries = allEntries.concat(batch);
          batch = await readBatch();
        }
      } catch (err) {
        const dirName = currentPath ? `${currentPath}/${entry.name}` : entry.name;
        console.warn('Skipping unreadable dropped folder', dirName, err);
        failed.push(dirName);
        return;
      }

      const newPath = currentPath ? `${currentPath}/${entry.name}` : entry.name;
      for (const child of allEntries) {
        await this.traverseEntry(child, files, newPath, failed);
      }
    }
  }
}
